/**
 * Admin Domain
 * The admin tools' actions on the server: resources for the team, the next
 * tick now, the innovation lock, and the match's end (devtools.go). No role is
 * checked, here or on the server: testing is greenfield.
 *
 * Each goes through the command gate (command-gate.ts), one at a time, and
 * waits (`pending`) until the server answers it, the gate drops the send for
 * another command, or the socket drops with it (the gate's end). The server
 * tells every player of a success, naming who asked (`sender_user_id`); only
 * that player is told in a toast, and only of what nothing else on screen
 * says — the lock's switch shows the lock, the end lobby the match's end. A
 * refusal comes to the player who asked alone: its title in their language,
 * the server's English under it, since the server's admin errors have no
 * codes.
 * Follows the project's unidirectional data flow pattern.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { OpCode, MessageType } from '../constants/op-codes';
import i18n from '../i18n/config';
import { nakamaService } from '../services/nakama/nakama-service';
import { sendGatedCommand, type GatedRequest } from './command-gate';
import { getViewerUserId } from './current-team';
import { handleInnovationSpendLock } from './financials';
import { handleTeamUpdate } from './teams';
import type { Team } from './teams';
import { showErrorToast, showSuccessToast } from './toasts';

// ==================== TYPES ====================

/** An action the admin tools send: the three resources each its own, as each is its own item. */
export type AdminAction = 'cash' | 'influence' | 'knowledge' | 'jumpTick' | 'innovationLock' | 'forceEnd';

/** What Add cash adds, in millions: a billion dollars. */
export const ADMIN_CASH_MILLIONS = 1000;
/** What Add Influence and Add Knowledge add. */
export const ADMIN_POINTS = 200;

interface AdminState {
  /** The action sent, until the server answers it: the window spins its item and rests the others. */
  pending: AdminAction | null;
  /** Forget it: the player has left the match (connection-reset.ts). */
  reset: () => void;
}

// ==================== STORE ====================

export const useAdminStore = create<AdminState>()(
  devtools(
    (set) => ({
      pending: null,
      reset: () => set({ pending: null }, false, 'admin/reset'),
    }),
    { name: 'AdminStore' }
  )
);

/** The resources' three items share one command, and one answer. */
const RESOURCES: readonly AdminAction[] = ['cash', 'influence', 'knowledge'];

/**
 * The server answered a success to every player: the player who asked stops
 * waiting, and is told `said` when there is something to say.
 */
function answered(sender: string | undefined, actions: readonly AdminAction[], said?: string): void {
  if (!sender || sender !== getViewerUserId()) return;
  const { pending } = useAdminStore.getState();
  if (pending && actions.includes(pending)) useAdminStore.setState({ pending: null });
  if (said) showSuccessToast(said);
}

/** The server refused the action, to the player who asked alone: the wait ends, and the toast says why in the server's words. */
function refused(error: string): void {
  console.warn('[Admin] The server refused the action:', error);
  useAdminStore.setState({ pending: null });
  showErrorToast(i18n.t('admin.result.refused'), error);
}

// ==================== HANDLERS ====================

/** Resources added to a team (OpCode 911): every player takes the team's figures. */
export function handleAdminAddResourcesSuccess(data: { team: Team; cohort_id: string; sender_user_id?: string }): void {
  if (data.team && data.cohort_id) {
    handleTeamUpdate(data.cohort_id, data.team);
  }
  answered(data.sender_user_id, RESOURCES, i18n.t('admin.result.resourcesAdded'));
}

export function handleAdminAddResourcesFailed(data: { error: string }): void {
  refused(data.error);
}

/** The next tick scheduled to run now (OpCode 914). */
export function handleAdminJumpToNextTickSuccess(data: { message: string; sender_user_id?: string }): void {
  answered(data.sender_user_id, ['jumpTick'], i18n.t('admin.result.tickJumped'));
}

export function handleAdminJumpToNextTickFailed(data: { error: string }): void {
  refused(data.error);
}

/** The innovation lock set (OpCode 917): every player's tree follows it; the switch says it. */
export function handleAdminSetInnovationSpendLockedSuccess(data: { innovation_spend_locked: boolean; sender_user_id?: string }): void {
  handleInnovationSpendLock(data.innovation_spend_locked);
  answered(data.sender_user_id, ['innovationLock']);
}

export function handleAdminSetInnovationSpendLockedFailed(data: { error: string }): void {
  refused(data.error);
}

/** The match set to end on the next tick (OpCode 920): the end lobby says it. */
export function handleAdminForceEndMatchSuccess(data: { message: string; sender_user_id?: string }): void {
  answered(data.sender_user_id, ['forceEnd']);
}

export function handleAdminForceEndMatchFailed(data: { error: string }): void {
  refused(data.error);
}

// ==================== SERVICE METHODS ====================

/**
 * Sends an admin action through the command gate, holding it as `pending`
 * until the server answers. The gate ends the wait without an answer — the
 * send turned away for another command on its way, or lost with the socket —
 * and a send that cannot go at all says why in a toast.
 */
async function sendAdminAction(action: AdminAction, opCode: GatedRequest, message: object): Promise<void> {
  if (useAdminStore.getState().pending) return;
  useAdminStore.setState({ pending: action });

  await sendGatedCommand(opCode, JSON.stringify(message), {
    endWait: () => {
      if (useAdminStore.getState().pending === action) useAdminStore.setState({ pending: null });
    },
    notSent: ({ error }) => showErrorToast(error),
  });
}

/** Add a billion dollars, 200 Influence or 200 Knowledge to the player's team: Knowledge is split among its players online. */
export function addResource(resource: 'cash' | 'influence' | 'knowledge'): Promise<void> {
  return sendAdminAction(resource, OpCode.OpCodeAdminAddResources, {
    type: MessageType.MessageTypeAdminAddResources,
    cash: resource === 'cash' ? ADMIN_CASH_MILLIONS : 0,
    influence: resource === 'influence' ? ADMIN_POINTS : 0,
    knowledge: resource === 'knowledge' ? ADMIN_POINTS : 0,
  });
}

/** Run the next simulation tick now: the next financial report. */
export function jumpToNextTick(): Promise<void> {
  return sendAdminAction('jumpTick', OpCode.OpCodeAdminJumpToNextTick, {
    type: MessageType.MessageTypeAdminJumpToNextTick,
  });
}

/** Lock or unlock spending Knowledge on the innovation tree, for every team. */
export function setInnovationSpendLocked(locked: boolean): Promise<void> {
  return sendAdminAction('innovationLock', OpCode.OpCodeAdminSetInnovationSpendLocked, {
    type: MessageType.MessageTypeAdminSetInnovationSpendLocked,
    innovation_spend_locked: locked,
  });
}

/** End the match for every team on the next tick: they move to the end lobby. Only while it is being played. */
export function forceEndMatch(): Promise<void> {
  return sendAdminAction('forceEnd', OpCode.OpCodeAdminForceEndMatch, {
    type: MessageType.MessageTypeAdminForceEndMatch,
  });
}

/** Drop the connection without clearing anything, as a network fault would: the game reconnects on its own. */
export function simulateDisconnect(): void {
  nakamaService.simulateDisconnect();
}
